import { MediaFile } from "./mediaFile.model";

export type LayeredMediaPreview = {
  id?: number;
  name?: string;
  type?: "image" | "video";
  preview_media_id?: number | null;
  preview_url?: string | null;
};

export type VideoContent = {
  video_id: number;
  thumbnail_id: number;
  layer_id: number;
  layered_media_id?: number;
  layered_media?: LayeredMediaPreview | null;
  copy_text?: string;
  link_headline?: string;
  link_description?: string;
  cta_text?: string;
  link?: string;
  video?: MediaFile | null;
  thumbnail?: MediaFile | null;
};

export type ImageContent = {
  image_id: number;
  layer_id: number;
  layered_media_id?: number;
  layered_media?: LayeredMediaPreview | null;
  copy_text?: string;
  link_headline?: string;
  link_description?: string;
  cta_text?: string;
  link?: string;
  image?: MediaFile | null;
};

export function resolvePreviewMedia(c: AdContent | undefined): MediaFile | null {
  if (!c) return null;
  if ("thumbnail" in c && c.thumbnail) return c.thumbnail;
  if ("layered_media" in c && c.layered_media?.preview_url) {
    const lm = c.layered_media;
    return new MediaFile({
      id: String(lm.preview_media_id ?? lm.id ?? ""),
      storage_uri: lm.preview_url ?? null,
      file_type: "image",
      name: lm.name ?? "",
    });
  }
  return null;
}

export type DocumentContent = {};
export type FormContent = {};
export type EmailContent = {
  // Required to send: SES refuses a message without one, and the send refuses earlier with
  // something an author can act on.
  subject?: string;
  // The grey line a mail client shows after the subject in an inbox list.
  preheader?: string;
  html: string;
  // Optional. Generated from the HTML at send time when an author leaves it empty.
  text?: string;
};
export type SMSContent = {
  image_id?: number;
  text: string;
  image?: MediaFile | null;
};
export type AdType =
  | "video"
  | "image"
  | "document"
  | "form"
  | "email"
  | "sms"
  // A mail piece (MPN-1706). A leaf with no variants: one card, two sides of artwork.
  | "direct_mail"
  | "container";

export type DirectMailContent = {
  size: string;
  front_html: string;
  back_html: string;
  /** May contain {{code}}, replaced per card so a scan identifies one person. */
  qr_destination: string;
  /** Where the code sits and how big it is, in inches on the FINISHED card from the
      bottom-left -- the coordinate system Lob measures its qr_code parameter in. Null means
      the platform default placement, not the absence of a code. */
  qr_left?: number | null;
  qr_bottom?: number | null;
  qr_width?: number | null;
};

export type AdContent =
  | DirectMailContent
  | VideoContent
  | ImageContent
  | DocumentContent
  | FormContent
  | EmailContent
  | SMSContent;

/**
 * Why an ad's content can't be edited (MPN-1390). The two reasons differ in
 * reversibility: `is_approved` releases if the approval is withdrawn, `is_published`
 * never does. Content edits on a locked ad are refused with 409 `content_locked`.
 *
 * Only present on the ad the API was asked for — nested `children` come back without it.
 */
export type AdLock = {
  is_locked: boolean;
  is_approved: boolean;
  is_published: boolean;
};

/**
 * May this ad's content be written? `confirmed` is the user having accepted the "edit
 * anyway" warning. An absent lock means unlocked — the API omits it on nested children,
 * and the server is the real gate either way (409 `content_locked`).
 *
 * Kept as one predicate because the callers that matter most are the ones with no user
 * present: the autosave and the save-on-close in AdEditor must not overwrite approved
 * content just because nobody could be asked.
 */
export function contentEditAllowed(lock: AdLock | null | undefined, confirmed: boolean): boolean {
  return !lock?.is_locked || confirmed;
}

export class Ad {
  id: number;
  name: string;
  type: AdType;
  subtype?: AdType;
  is_default: boolean;
  is_template: boolean;
  content?: AdContent;
  children: Ad[];
  parent_id?: number | null;
  lock: AdLock | null;
  /** The template this ad was rebranded from. Internal UI shows it beside the name so a
   *  retitled ad can be traced to its source (MPN-1508). Clients never see these. */

  constructor(data?: Partial<Ad>) {
    this.id = data?.id ?? -1;
    this.name = data?.name ?? "";
    this.type = data?.type ?? "container";
    this.subtype = data?.subtype;
    this.is_default = data?.is_default ?? false;
    this.is_template = data?.is_template ?? false;
    this.content = data?.content;
    this.children = data?.children ?? [];
    this.parent_id = data?.parent_id;
    // Must be initialised for AdSerializer to copy it — _copyAttributesValue only walks
    // keys already present on the target.
    this.lock = data?.lock ?? null;
  }
}
